import React from "react"
import {
  Table as AntTable,
  Tag,
  Space,
  Tooltip,
  notification,
} from "antd"
import { EyeOutlined } from "@ant-design/icons"
import moment from "moment"
import { ExtraButtons } from "src/components/extra-buttons"
import { Header } from "src/components/layout/header"
import { VisibilityProvider } from "src/components/visibility-provider"
import { Button } from "src/components/actions/button"
import { usePermission } from "src/hooks"
import { CreateDistributionDrawer } from "src/pages-lib/bonus-distributions/create-drawer"
import { DetailDrawer } from "src/pages-lib/bonus-distributions/detail-drawer"
import api from "src/api"
import styled from "styled-components"

const statusMap = {
  pending: { color: "default", text: "Ожидание" },
  processing: { color: "processing", text: "В процессе" },
  completed: { color: "success", text: "Завершено" },
  failed: { color: "error", text: "Ошибка" },
}

const columns = (onView) => [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    width: 60,
    sorter: (a, b) => a.id - b.id,
    defaultSortOrder: "descend",
  },
  {
    title: "Название",
    dataIndex: "title",
    key: "title",
  },
  {
    title: "Тип",
    dataIndex: "lookup_type",
    key: "lookup_type",
    width: 100,
    render: (val) => (val === "phone" ? "Телефон" : "GUID"),
  },
  {
    title: "Баллы",
    dataIndex: "amount",
    key: "amount",
    width: 80,
  },
  {
    title: "Статус",
    dataIndex: "status",
    key: "status",
    width: 130,
    render: (val) => {
      const s = statusMap[val] || { color: "default", text: val }
      return <Tag color={s.color}>{s.text}</Tag>
    },
  },
  {
    title: "Всего",
    dataIndex: "total_rows",
    key: "total_rows",
    width: 70,
  },
  {
    title: "Отправлено",
    dataIndex: "sent_count",
    key: "sent_count",
    width: 100,
    render: (val) => <Tag color="green">{val}</Tag>,
  },
  {
    title: "Ошибки",
    dataIndex: "failed_count",
    key: "failed_count",
    width: 80,
    render: (val) =>
      val > 0 ? <Tag color="red">{val}</Tag> : <Tag>{val}</Tag>,
  },
  {
    title: "Дата",
    dataIndex: "created_at",
    key: "created_at",
    width: 160,
    render: (val) => val ? moment(val).format("DD.MM.YYYY HH:mm") : "—",
  },
  {
    title: "",
    key: "action",
    width: 50,
    render: (_, record) => (
      <Tooltip title="Подробнее">
        <Button
          size="small"
          type="link"
          onClick={() => onView(record.id)}
          icon={<EyeOutlined />}
        />
      </Tooltip>
    ),
  },
]

export default function BonusDistributions() {
  const { hasPermission } = usePermission()
  const [createDrawerState, setCreateDrawerState] = React.useState(false)
  const [detailDrawerState, setDetailDrawerState] = React.useState(false)
  const [detailId, setDetailId] = React.useState(null)
  const [items, setItems] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [pagination, setPagination] = React.useState(null)

  React.useEffect(() => {
    loadItems()
  }, [])

  async function loadItems(page = 1, countOnPage = 20) {
    setIsLoading(true)
    try {
      const { data } = await api.get(
        `bonus-distributions?page=${page}&countOnPage=${countOnPage}`,
        { useV2Api: true }
      )
      setItems(data.items ?? [])
      setPagination({
        current: data.pagination.currentPage,
        pageSize: data.pagination.countOnPage,
        total: data.pagination.total,
      })
    } catch (error) {
      console.error("Ошибка загрузки", error)
    } finally {
      setIsLoading(false)
    }
  }

  function onView(id) {
    setDetailId(id)
    setDetailDrawerState(true)
  }

  function onCreated() {
    setCreateDrawerState(false)
    loadItems()
  }

  return (
    <>
      <Header
        title="Рассылка баллов"
        extra={
          <ExtraButtons
            onCreate={
              hasPermission("bonus-distributions.create")
                ? () => setCreateDrawerState(true)
                : undefined
            }
            titles={{ onCreate: "Новая рассылка" }}
          />
        }
      />

      <StyledTable
        dataSource={items.map((i) => ({ ...i, key: i.id }))}
        columns={columns(onView)}
        loading={isLoading}
        pagination={pagination}
        onChange={(pag) => loadItems(pag.current, pag.pageSize)}
        size="small"
      />

      <VisibilityProvider visible={createDrawerState}>
        <CreateDistributionDrawer
          onClose={() => setCreateDrawerState(false)}
          onSuccess={onCreated}
        />
      </VisibilityProvider>

      <VisibilityProvider visible={detailDrawerState}>
        <DetailDrawer
          distributionId={detailId}
          onClose={() => {
            setDetailDrawerState(false)
            setDetailId(null)
          }}
        />
      </VisibilityProvider>
    </>
  )
}

const StyledTable = styled(AntTable)``
